/**
 * @file    lineChartOptionsBuilder
 * @scope   SHARED
 * @summary Pure helpers that turn a table into ECharts line-chart options (both tables).
 * @consumes helpers/utilities, constants/chartOption
 * @produces extractTextFromNode, getPercentageFlag, getCellRawValue, buildLineChartOptions
 * @sideEffects None.
 */
import { formatValueByDecimalPlaces } from 'helpers/utilities';
import { legendCommon, legendTextStyle } from 'constants/chartOption';
import { GoalValues, LineChartData } from '../types';

/** Flattens a React node (header element) into its plain text. */
export const extractTextFromNode = (node: any): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node
      .map((child) => extractTextFromNode(child))
      .join(' ')
      .trim();
  }
  if (typeof node === 'object' && 'props' in node) {
    return extractTextFromNode(node.props?.children);
  }
  return '';
};

export const getColumnFieldKey = (col: any) => col?.sortField || col?.accessorKey || col?.id;

export const getRawColumnByField = (rawData: any, fieldKey: string) =>
  rawData?.columns?.find((rawCol: any) => rawCol?.field === fieldKey);

const hasPercentageMetadata = (value: any) =>
  !!(
    value &&
    typeof value === 'object' &&
    ('isPercentage' in value ? value.isPercentage : value?.percentage ?? value?.isPercent)
  );

/** Percentage flag of a column: raw column metadata first, then the sample cell. */
export const getPercentageFlag = (rawData: any, fieldKey: string, rowSample?: any) => {
  const rawColumn = getRawColumnByField(rawData, fieldKey);
  if (rawColumn) {
    return !!rawColumn.isPercentage || !!rawColumn.percentage || !!rawColumn.isPercent;
  }
  return hasPercentageMetadata(rowSample?.[fieldKey]);
};

const formatLineChartValue = (value: any, decimalPlaces: any, isPercentage = false) => {
  const formattedValue = formatValueByDecimalPlaces(value, Number(decimalPlaces));
  return formattedValue === undefined || formattedValue === null
    ? ''
    : `${formattedValue}${isPercentage ? '%' : ''}`;
};

const lastValue = (value: any) => (Array.isArray(value) ? value[value.length - 1] : value);

const getTooltipPointValue = (point: any) => (point ? lastValue(point.value) : '');

const getSeriesName = (row: any, firstColumnField: string | undefined, idx: number) => {
  const rowLabelRaw = firstColumnField ? row?.[firstColumnField] : undefined;
  const rowLabelValue =
    typeof rowLabelRaw === 'object' && rowLabelRaw && 'value' in rowLabelRaw
      ? rowLabelRaw.value
      : rowLabelRaw;
  const rowLabel =
    rowLabelValue === null || rowLabelValue === undefined ? '' : String(rowLabelValue).trim();
  return rowLabel !== '' ? rowLabel : `Row ${idx + 1}`;
};

const buildLegend = (eyeIconBase64: string) => ({
  ...legendCommon,
  show: true,
  bottom: 5,
  type: 'scroll',
  textStyle: { ...legendTextStyle },
  selector: [{ type: 'all', title: '{icon|}{a|Show All}' }],
  selectorLabel: {
    show: true,
    rich: {
      a: { fontSize: 11 },
      icon: { width: 30, backgroundColor: { image: eyeIconBase64 } },
    },
    borderWidth: 0,
  },
  emphasis: {
    selectorLabel: { color: '#8a8a8a', backgroundColor: 'transparent' },
  },
  selectorPosition: 'end',
  selectedMode: true,
});

export interface LineChartOptionsArgs {
  chartData: LineChartData;
  rows: any[];
  firstColumnField?: string;
  decimalPlaces: any;
  goalValues: GoalValues;
  drawGoalLine: boolean;
  yAxisValues: any;
  eyeIconBase64: string;
}

export const buildLineChartOptions = ({
  chartData: { xData, yData, percentageFlags },
  rows,
  firstColumnField,
  decimalPlaces,
  goalValues,
  drawGoalLine,
  yAxisValues,
  eyeIconBase64,
}: LineChartOptionsArgs) => {
  if (!xData.length || !yData.length) return {};

  const isPercentageAt = (index?: number) => percentageFlags[index ?? 0] ?? false;
  const { lineStyle, lineWidth, goalLineColor, goalValue } = goalValues || {};

  const markLine = {
    symbol: 'none',
    lineStyle: {
      type: lineStyle ? lineStyle : 'solid',
      width: lineWidth && drawGoalLine ? lineWidth : 0,
      color: goalLineColor ? goalLineColor : '#000',
    },
    data: [
      {
        label: { show: false },
        tooltip: { formatter: '<strong>Goal{b}:</strong> {c}' },
        yAxis: goalValue ? goalValue : 0,
      },
    ],
  };

  const label = {
    show: true,
    position: 'top',
    formatter: (params: any) =>
      formatLineChartValue(
        lastValue(params?.value),
        decimalPlaces,
        isPercentageAt(params?.dataIndex)
      ),
  };

  return {
    legend: buildLegend(eyeIconBase64),
    grid: { top: 20, bottom: 60, containLabel: true },
    xAxis: { type: 'category', data: xData },
    yAxis: { type: 'value', ...yAxisValues },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const points = Array.isArray(params) ? params : [params];
        if (!points.length) return '';

        const axisLabel = points[0]?.axisValueLabel ?? points[0]?.axisValue ?? '';
        const seriesLines = points.map((point: any) => {
          const value = formatLineChartValue(
            getTooltipPointValue(point),
            decimalPlaces,
            isPercentageAt(point?.dataIndex)
          );
          return `${point?.marker ?? ''}${point?.seriesName ?? ''}: ${value}`;
        });

        return [axisLabel, ...seriesLines].join('<br/>');
      },
    },
    series: yData.map((data, idx) => ({
      type: 'line',
      data,
      name: getSeriesName(rows[idx], firstColumnField, idx),
      markLine,
      label,
    })),
  };
};
